import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormTrainingPackageRadioGroup } from '../../Form/FormTrainingPackageRadioGroup';
import { ONBOARDING_TRAINING_PACKAGE_OPTIONS } from '../onboardingConstants';
import { OnboardingStepHeader } from '../OnboardingStepHeader';
import { OnboardingStepScrollLayout } from '../OnboardingStepScrollLayout';
import { useOnboardingLens } from '../onboardingLens';
import type { OnboardingTrainingPackageValue } from '../OnboardingWizard.types';

interface OnboardingStepTrainingPackageProps {
  trainingPackage: OnboardingTrainingPackageValue | '';
  onTrainingPackageChange: (value: OnboardingTrainingPackageValue) => void;
  showErrors: boolean;
  fieldErrors: {
    trainingPackage?: string;
  };
}

export function OnboardingStepTrainingPackage({
  trainingPackage,
  onTrainingPackageChange,
  showErrors,
  fieldErrors,
}: OnboardingStepTrainingPackageProps) {
  const { t } = useTranslation();
  const { layout } = useOnboardingLens();
  const { formSection } = layout;

  const packageError =
    showErrors && fieldErrors.trainingPackage
      ? t(`onboarding.wizard.step2.errors.${fieldErrors.trainingPackage}`)
      : undefined;

  const options = useMemo(
    () =>
      ONBOARDING_TRAINING_PACKAGE_OPTIONS.map((option) => ({
        value: option.value,
        title: t(`onboarding.wizard.step2.options.${option.labelKey}.title`),
        description: t(`onboarding.wizard.step2.options.${option.labelKey}.description`),
      })),
    [t],
  );

  return (
    <OnboardingStepScrollLayout
      layout="centered"
      px={formSection.paddingX}
      py={formSection.paddingY}
      contentPaddingBottom={formSection.headerSubtitleBottomSpacing}
      header={
        <OnboardingStepHeader
          title={t('onboarding.wizard.step2.title')}
          subtitle={t('onboarding.wizard.step2.subtitle')}
          titleSubtitleGap={formSection.headerSubtitleGap}
        />
      }
    >
      <FormTrainingPackageRadioGroup
        name="onboarding-training-package"
        value={trainingPackage}
        options={options}
        onChange={(value) => onTrainingPackageChange(value as OnboardingTrainingPackageValue)}
        error={packageError}
        aria-label={t('onboarding.wizard.step2.title')}
      />
    </OnboardingStepScrollLayout>
  );
}
